import React from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle, Shield, Zap, FileText, Clock, TrendingUp, Users, Briefcase,
  Home, Heart, Gift, RefreshCw, ArrowUpCircle, Lightbulb, Star, Phone, Mail,
  MapPin, AlertTriangle, Headphones, Building, DollarSign, Percent, Calendar,
  CreditCard, Smartphone, Award, ThumbsUp, Handshake, BookOpen, Globe, Lock,
  Wrench
} from 'lucide-react';

const HomeLoanVaranasi = () => {
  return (
    <div className="bg-gray-50">
      <div className="relative bg-gradient-to-r from-green-800 to-green-700 text-white py-12 md:py-16 overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="text-center lg:text-left">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 animate-fade-in-up">
                Home Loan in Varanasi
              </h1>
              <p className="text-lg md:text-xl mb-6 animate-fade-in-up animation-delay-200">
                Your dream home is closer than you think. Get affordable home loans with quick approval and flexible EMIs.
              </p>
              <div className="flex flex-wrap gap-4 justify-center lg:justify-start animate-fade-in-up animation-delay-400">
                <button className="bg-yellow-400 hover:bg-yellow-300 text-gray-800 font-semibold px-6 py-2 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg">
                  Apply Now
                </button>
                <button className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white font-semibold px-6 py-2 rounded-full transition-all duration-300">
                  Check Eligibility
                </button>
              </div>
            </div>
            <div className="flex justify-center lg:justify-end">
              <img
                src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Home Loan"
                className="max-w-full h-auto rounded-2xl shadow-xl transition-transform duration-500 hover:scale-105"
              />
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-12">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" fill="white"></path>
          </svg>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
         <div className="mb-16">
          <h2 className="text-3xl font-bold text-green-700 mb-6 text-center">About Home Loan</h2>
          <div className="bg-white rounded-xl shadow-md p-8">
            <p className="text-gray-700 leading-relaxed">
              You take out a home loan to buy a house from a financial institution. You can get a home loan to purchase a house or apartment that's ready to move in or one that's under construction. You can get home loans from banks and non-banking financial companies (NBFCs) or Home Loan Service Providers in Varanasi. These have varying interest rates that vary depending on your credit score. In most cases, home loans have a 30-year term and require repayment in Equated Monthly Installments. Tax breaks are also available under Sections 80C and 24 of the Income Tax Act for interest and principal on your home loan. Homeowners can apply for a loan to purchase, build, or improve their homes.
            </p>
          </div>
        </div>

        {/* Advantages */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-green-700 mb-8 text-center">Advantages of Home Loan from  CashPro Financial Services</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Percent, title: "Attractive Interest Rates", desc: "Home loan interest rates as low as 6.75% p.a. for both salaried and self-employed individuals." },
              { icon: Home, title: "Loan for Everything", desc: "Purchase, construction, renovation, extension – we cover all your housing needs." },
              { icon: Calendar, title: "Long Tenure", desc: "Get loans up to 30 years for new homes, up to 20 years for other purposes." },
              { icon: DollarSign, title: "Easy Financing", desc: "Finance up to 90% of the property value; you only arrange the remaining 10%." },
              { icon: Percent, title: "Low Processing Fees", desc: "Only 0.5% of the loan amount as processing fee (industry average is 1-2%)." },
              { icon: Smartphone, title: "Convenient Features", desc: "Doorstep service, online portal, video KYC, and digital onboarding." },
              { icon: Users, title: "Customized Eligibility", desc: "Surrogate programs designed to suit your unique financial profile." },
              { icon: Globe, title: "National Presence", desc: "Pan-India service with a strong local network in Varanasi and beyond." },
              { icon: ArrowUpCircle, title: "Loan Enhancements", desc: "Upgrade your loan if construction costs increase or need additional funds." },
              { icon: Headphones, title: "Improved Customer Experience", desc: "24/7 customer service via phone, email, and mobile app." },
              { icon: Shield, title: "Valuable Additions", desc: "Optional insurance coverage to protect you and your property." },
              { icon: ThumbsUp, title: "Quick Disbursal", desc: "Fast processing and disbursal once documents are verified." }
            ].map((item, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <item.icon className="w-10 h-10 text-green-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-green-700 mb-8 text-center">Different Kinds of Home Loans</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { icon: Home, title: "Home Purchase Loan", desc: "Loans for acquisition, expansion, and development of residential properties." },
              { icon: Building, title: "Home Construction Loan", desc: "Build your dream home on your own land." },
              { icon: ArrowUpCircle, title: "Home Extension Loan", desc: "Add extra rooms, a guest house, or expand your existing home." },
              { icon: Wrench, title: "Home Improvement Loan", desc: "Renovate, repair, upgrade interiors, plumbing, electrical works, etc." },
              { icon: RefreshCw, title: "Home Conversion Loan", desc: "Transfer your existing home loan to a new property with better terms." },
              { icon: MapPin, title: "Land Purchase Loan", desc: "Finance the purchase of a plot or piece of land for residential use." },
              { icon: Clock, title: "Bridge Loans", desc: "Short-term financing to cover immediate liquidity needs until permanent financing is arranged." }
            ].map((item, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300">
                <item.icon className="w-10 h-10 text-green-600 mb-3" />
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="mb-16 bg-white rounded-xl shadow-md p-8">
          <h2 className="text-3xl font-bold text-green-700 mb-6 text-center">Eligibility Criteria for Home Loan</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              "Indian citizen with regular source of income (salaried, self-employed, or business owner).",
              "Minimum age: 21 years; maximum age at loan maturity: 60-70 years (varies by lender).",
              "Stable employment and consistent savings history.",
              "Credit score preferably 750 or above.",
              "For self-employed: profit and turnover records are considered.",
              "Property must be legally approved and free from encumbrances."
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="mb-16 bg-white rounded-xl shadow-md p-8">
          <h2 className="text-3xl font-bold text-green-700 mb-6 text-center">Documents You Will Need</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              "Duly filled and signed application form.",
              "Passport-size photographs (2-3).",
              "Proof of Identity: PAN Card, Passport, Voter ID, Aadhaar, Driving License.",
              "Proof of Address: Aadhaar, Passport, Voter ID, Utility bills (not older than 3 months), Rent agreement.",
              "Proof of Age: Birth certificate, Class 10 certificate, PAN Card, Passport.",
              "Income Proof (Salaried): Salary slips (last 3 months), Form 16, Bank statements (last 6 months).",
              "Income Proof (Self-Employed): ITR (last 3 years), Profit & Loss statement, Balance Sheet, Bank statements.",
              "Property Documents: Sale agreement, Allotment letter, NOC from builder/society, Approved building plan.",
              "Valuation report from bank's empaneled valuer (if applicable).",
              "Any other documents requested by the lender."
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="mb-16 bg-gradient-to-r from-green-50 to-green-50 rounded-xl p-8">
          <h2 className="text-3xl font-bold text-green-700 mb-6 text-center">What Can We Do For You?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "We provide an unbiased platform to compare home loan offers from multiple lenders.",
              "Our experts analyze your profile to recommend the best loan option.",
              "We help you check eligibility before approaching any bank.",
              "We assist with documentation to ensure everything is complete and accurate.",
              "We coordinate with lenders for faster processing and approval.",
              "We offer post-disbursal support and guidance."
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <Lightbulb className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-2xl p-8 shadow-lg text-center">
          <h2 className="text-3xl font-bold text-green-700 mb-4">We Are Here to Help You</h2>
          <p className="text-gray-600 mb-6">Have questions? Our home loan experts are ready to assist you.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div>
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">APPLY FOR LOAN</p>
              <p className="text-gray-800 font-medium mt-1">Ready to own your dream home?</p>
              <button className="mt-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-2 rounded-full transition duration-300 shadow">
                Apply Now
              </button>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">CALL US AT</p>
              <a href="tel:+918887141052" className="text-lg font-bold text-green-600 hover:underline">
                +91 88871 41052
              </a>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">MAIL US AT</p>
              <a href="mailto:info@cashpro.co.in" className="text-lg font-bold text-green-600 hover:underline">
                info@cashpro.co.in
              </a>
            </div>
          </div>
          <Link to="/contact" className="inline-block text-green-600 hover:underline font-medium">
            Contact Us →
          </Link>
        </div>
      </div>

      <style jsx>{`
        @keyframes fade-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-up {
          animation: fade-up 0.6s ease-out forwards;
        }
        .animate-fade-in-up {
          animation: fade-up 0.6s ease-out forwards;
        }
        .animation-delay-200 {
          animation-delay: 0.2s;
        }
        .animation-delay-400 {
          animation-delay: 0.4s;
        }
      `}</style>
    </div>
  );
};

export default HomeLoanVaranasi;